import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiErr, API } from "@/api";
import Shell from "@/components/Shell";
import { visitorNav } from "./VisitorDirectory";
import CatalogueModal from "@/components/CatalogueModal";
import { Spinner, Overline } from "@/components/Brand";
import { toast } from "sonner";
import { MapPin, CheckCircle2, FileText, Sparkles, BookmarkPlus, Heart } from "lucide-react";

export default function ExhibitorDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ex, setEx] = useState(null);
  const [saved, setSaved] = useState([]);
  const [checking, setChecking] = useState(false);
  const [viewCat, setViewCat] = useState(null);

  const load = async () => {
    const { data } = await api.get(`/exhibitors/${id}`);
    setEx(data);
    const { data: s } = await api.get("/visitor/saved-catalogues");
    setSaved(s.map((c) => c.id));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const checkin = async () => {
    setChecking(true);
    try {
      await api.post("/visitor/checkin", { exhibitor_id: id, source: "manual" });
      toast.success(`Checked in at ${ex.name}`);
    } catch (err) { toast.error(apiErr(err)); }
    finally { setChecking(false); }
  };

  const saveCat = async (cid) => {
    try {
      const { data } = await api.post(`/visitor/saved-catalogues/${cid}`);
      setSaved((p) => (data.saved ? [...p, cid] : p.filter((x) => x !== cid)));
      toast.success(data.saved ? "Catalogue saved" : "Removed");
    } catch (err) { toast.error(apiErr(err)); }
  };

  if (!ex) return <Shell nav={visitorNav}><Spinner /></Shell>;

  return (
    <Shell nav={visitorNav}>

      <div className="rounded-3xl overflow-hidden glass">
        <div className="h-48 md:h-64 bg-cover bg-center relative" style={{ backgroundImage: `url(${ex.logo_url})` }}>
          <div className="absolute inset-0 bg-gradient-to-t from-[#07130F] to-transparent" />
          <div className="absolute bottom-4 left-5 right-5">
            <Overline>{ex.hall} · {ex.booth}</Overline>
            <h1 className="font-display text-3xl sm:text-4xl text-slate-100 mt-1">{ex.name}</h1>
          </div>
        </div>
        <div className="p-5">
          <div className="flex items-center gap-1 text-sm text-slate-400"><MapPin size={14} /> {ex.city}</div>
          <p className="text-slate-300 mt-3 leading-relaxed">{ex.description}</p>
          <div className="flex flex-wrap gap-2 mt-4">
            {ex.categories.map((c) => (
              <span key={c} className="text-xs px-3 py-1 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/20">{c}</span>
            ))}
          </div>
          <button data-testid="visitor-checkin-button" onClick={checkin} disabled={checking} className="w-full btn-gold py-3 rounded-xl mt-5 flex items-center justify-center gap-2 disabled:opacity-60">
            <CheckCircle2 size={18} /> {checking ? "Checking in…" : "Check In Here"}
          </button>
        </div>
      </div>

      <h2 className="font-display text-2xl gold-text mt-8">Catalogues</h2>
      {ex.catalogues?.length ? (
        <div className="space-y-3 mt-4">
          {ex.catalogues.map((c) => (
            <div key={c.id} className="glass rounded-2xl p-4 flex items-center gap-3" data-testid={`catalogue-${c.id}`}>
              <div className="h-11 w-11 rounded-xl bg-amber-500/10 flex items-center justify-center shrink-0">
                {c.type === "ai" ? <Sparkles size={18} className="text-amber-400" /> : <FileText size={18} className="text-amber-400" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-slate-100 font-medium truncate">{c.title}</div>
                <div className="text-[11px] text-slate-500 uppercase tracking-wide">{c.type === "ai" ? "AI Catalogue" : "PDF"}</div>
              </div>
              {c.type === "pdf" && c.storage_path && (
                <a href={`${API}/files/${c.storage_path}`} target="_blank" rel="noreferrer" className="text-xs text-amber-300 underline" data-testid={`open-pdf-${c.id}`}>Open</a>
              )}
              {c.type === "ai" && (
                <button onClick={() => setViewCat(c)} className="text-xs text-amber-300 underline" data-testid={`view-ai-${c.id}`}>View</button>
              )}
              <button onClick={() => saveCat(c.id)} className="h-9 w-9 rounded-lg glass flex items-center justify-center" data-testid={`save-catalogue-${c.id}`}>
                {saved.includes(c.id) ? <Heart size={15} className="fill-amber-400 text-amber-400" /> : <BookmarkPlus size={15} className="text-slate-300" />}
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-slate-400 text-sm mt-3">No catalogues published yet.</p>
      )}

      {viewCat && <CatalogueModal catalogue={{ ...viewCat, exhibitor_name: viewCat.exhibitor_name || ex.name }} visitor onClose={() => setViewCat(null)} />}
    </Shell>
  );
}
